import React, { useState, useEffect } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import SortableMenuItem from './sortable-menu-item'
import MenuItemModal from './menu-item-modal'

function MenuEditor () {
  const [menus, setMenus] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingItem, setEditingItem] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [activeId, setActiveId] = useState(null)
  const [overId, setOverId] = useState(null)
  const [expandedItems, setExpandedItems] = useState(new Set())
  const [isReordering, setIsReordering] = useState(false)
  const [menuVersion, setMenuVersion] = useState(0) // To force mega-menu refresh

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Fetch menus on mount
  useEffect(() => {
    fetchMenus()
  }, [])

  const fetchMenus = async () => {
    try {
      const response = await fetch('/menus.json', {
        headers: {
          Accept: 'application/json'
        }
      })
      if (!response.ok) throw new Error('Failed to fetch menus')
      const data = await response.json()
      setMenus(data.menus || [])
      setLoading(false)
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  const handleDragStart = event => {
    if (!isReordering) {
      setActiveId(event.active.id)
    }
  }

  const [dragOverInfo, setDragOverInfo] = useState(null)

  const handleDragOver = event => {
    const { active, over, delta, activatorEvent } = event
    if (!over) {
      setOverId(null)
      setDragOverInfo(null)
      return
    }

    setOverId(over.id)

    // Get the dragged element position
    const overElement = document.querySelector(
      `[data-sortable-id="${over.id}"]`
    )
    if (overElement && activatorEvent) {
      const rect = overElement.getBoundingClientRect()
      const mouseY = activatorEvent.clientY + delta.y
      const relativeY = mouseY - rect.top
      const isTopHalf = relativeY < rect.height / 2

      setDragOverInfo({
        id: over.id,
        isTopHalf: isTopHalf
      })
    }
  }

  const handleDragEnd = async event => {
    const { active, over } = event
    setActiveId(null)
    setOverId(null)
    const finalDragOverInfo = dragOverInfo
    setDragOverInfo(null)

    if (!over || active.id === over.id || isReordering) return

    // Find the item being moved
    const draggedItem = findMenuItem(menus, active.id)
    if (!draggedItem) return

    // Find the target item
    const targetItem = findMenuItem(menus, over.id)
    if (!targetItem) return

    let newParentId
    let newPosition

    // If dropping on top half, insert as sibling before the target
    if (finalDragOverInfo && finalDragOverInfo.isTopHalf) {
      newParentId = targetItem.parent_id || 'root'
      // Find all siblings of the target
      const allSiblings = getAllSiblings(menus, targetItem.parent_id)
      newPosition = allSiblings.findIndex(item => item.id === targetItem.id)
      if (newPosition === -1) newPosition = 0
    } else {
      // Otherwise, make it a child of the target
      newParentId = over.id
      newPosition = 0
    }

    // Don't move if dropping on itself or its own children
    if (
      newParentId !== 'root' &&
      isDescendant(draggedItem, newParentId, menus)
    ) {
      showToast('Cannot move item into its own children', 'error')
      return
    }

    // Set reordering state
    setIsReordering(true)

    // Send reorder request to backend
    try {
      const response = await fetch(`/menus/${active.id}/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          parent_id: newParentId === 'root' ? null : newParentId,
          position: newPosition
        })
      })

      if (!response.ok) throw new Error('Failed to reorder')

      // Auto-expand the parent item to show the moved item
      if (newParentId !== 'root' && !finalDragOverInfo?.isTopHalf) {
        setExpandedItems(prev => new Set([...prev, newParentId]))
      }

      // Refresh menus after successful reorder
      await fetchMenus()
      setMenuVersion(prev => prev + 1) // Force mega-menu refresh
      showToast('Menu item moved successfully', 'success')
    } catch (err) {
      showToast('Failed to reorder menu item', 'error')
      console.error('Reorder error:', err)
    } finally {
      setIsReordering(false)
    }
  }

  const findMenuItem = (items, id) => {
    for (const item of items) {
      if (item.id === id) return item
      if (item.children?.length) {
        const found = findMenuItem(item.children, id)
        if (found) return found
      }
    }
    return null
  }

  const isDescendant = (item, potentialParentId, allItems) => {
    if (!potentialParentId || !item.children?.length) return false

    const checkChildren = children => {
      for (const child of children) {
        if (child.id === potentialParentId) return true
        if (child.children?.length && checkChildren(child.children)) return true
      }
      return false
    }

    return checkChildren(item.children)
  }

  const getAllSiblings = (items, parentId) => {
    const siblings = []
    const collectSiblings = (itemList, currentParentId) => {
      for (const item of itemList) {
        if ((item.parent_id || null) === (parentId || null)) {
          siblings.push(item)
        }
        if (item.children?.length) {
          collectSiblings(item.children, item.id)
        }
      }
    }
    collectSiblings(items, null)
    return siblings
  }

  const handleAddItem = (parentId = null) => {
    setEditingItem({
      id: null,
      name: '',
      path: '',
      parent_id: parentId
    })
    setShowModal(true)
  }

  const handleEditItem = item => {
    setEditingItem(item)
    setShowModal(true)
  }

  const handlePromoteToRoot = async id => {
    // Find the item being promoted
    const item = findMenuItem(menus, id)
    if (!item || !item.parent_id) return

    // Set reordering state
    setIsReordering(true)

    try {
      const response = await fetch(`/menus/${id}/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          parent_id: null,
          position: 0
        })
      })

      if (!response.ok) throw new Error('Failed to promote to root')

      // Refresh menus after successful promotion
      await fetchMenus()
      setMenuVersion(prev => prev + 1)
      showToast('Menu item promoted to root level', 'success')
    } catch (err) {
      showToast('Failed to promote menu item', 'error')
      console.error('Promote error:', err)
    } finally {
      setIsReordering(false)
    }
  }

  const handleDeleteItem = async id => {
    if (
      !confirm(
        'Are you sure you want to delete this menu item and all its children?'
      )
    )
      return

    try {
      const response = await fetch(`/menus/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })

      if (!response.ok) throw new Error('Failed to delete')

      await fetchMenus()
      setMenuVersion(prev => prev + 1) // Force mega-menu refresh
      showToast('Menu item deleted successfully', 'success')
    } catch (err) {
      showToast('Failed to delete menu item', 'error')
      console.error('Delete error:', err)
    }
  }

  const handleSaveItem = async item => {
    const isNew = !item.id
    const url = isNew ? '/menus' : `/menus/${item.id}`
    const method = isNew ? 'POST' : 'PATCH'

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ menu: item })
      })

      if (!response.ok) throw new Error('Failed to save')

      await fetchMenus()
      setShowModal(false)
      setMenuVersion(prev => prev + 1) // Force mega-menu refresh
      showToast(
        `Menu item ${isNew ? 'created' : 'updated'} successfully`,
        'success'
      )
    } catch (err) {
      showToast(`Failed to ${isNew ? 'create' : 'update'} menu item`, 'error')
      console.error('Save error:', err)
    }
  }

  const toggleExpanded = id => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const showToast = (message, type = 'info') => {
    // Simple toast implementation - you might want to use a library
    const toast = document.createElement('div')
    toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
      type === 'success'
        ? 'bg-green-500 text-white'
        : type === 'error'
        ? 'bg-red-500 text-white'
        : 'bg-blue-500 text-white'
    }`
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 3000)
  }

  const renderMenuTree = (items, parentId = null, level = 0) => {
    const sortedItems = items.filter(
      item => (item.parent_id || null) === parentId
    )

    return (
      <SortableContext
        items={sortedItems.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={level > 0 ? 'ml-8' : ''}>
          {sortedItems.map((item, index) => (
            <div key={item.id}>
              {/* Drop indicator before item */}
              {dragOverInfo &&
                dragOverInfo.id === item.id &&
                dragOverInfo.isTopHalf && (
                  <div className='relative mb-2'>
                    <div className='h-1 bg-blue-500 rounded animate-pulse' />
                    <div className='absolute -top-6 left-0 bg-blue-600 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap'>
                      Insert before "{item.name}"
                    </div>
                  </div>
                )}

              <SortableMenuItem
                item={item}
                level={level}
                index={index}
                onEdit={handleEditItem}
                onDelete={handleDeleteItem}
                onAddChild={() => handleAddItem(item.id)}
                onPromoteToRoot={handlePromoteToRoot}
                expanded={expandedItems.has(item.id)}
                onToggleExpanded={toggleExpanded}
                hasChildren={items.some(child => child.parent_id === item.id)}
                isOver={
                  overId === item.id &&
                  (!dragOverInfo || !dragOverInfo.isTopHalf)
                }
                isDragging={activeId === item.id}
                disabled={isReordering}
              >
                {expandedItems.has(item.id) &&
                  renderMenuTree(items, item.id, level + 1)}
              </SortableMenuItem>
            </div>
          ))}
        </div>
      </SortableContext>
    )
  }

  // Flatten menu tree for DnD context
  const flattenMenus = items => {
    const flat = []
    const addItems = items => {
      items.forEach(item => {
        flat.push(item)
        if (item.children) addItems(item.children)
      })
    }
    addItems(items)
    return flat
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-gray-500 dark:text-gray-400'>Loading menus...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-4'>
        <p className='text-red-800 dark:text-red-200'>Error: {error}</p>
      </div>
    )
  }

  const allItems = flattenMenus(menus)

  return (
    <div className='max-w-4xl mx-auto relative'>
        {/* Progress Overlay */}
        {isReordering && (
          <div className='absolute inset-0 bg-gray-900/50 dark:bg-gray-900/70 rounded-lg z-50 flex items-center justify-center'>
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 flex items-center space-x-4'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
              <span className='text-gray-900 dark:text-white font-medium'>
                Reordering menu item...
              </span>
            </div>
          </div>
        )}

        <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-4'>
                <h2 className='text-lg font-medium text-gray-900 dark:text-white'>
                  Menu Editor
                </h2>
                {/* Embed the mega-menu directly */}
                <div key={menuVersion} className='flex-1'>
                  <nfca-mega-menu />
                </div>
              </div>
              <button
                onClick={() => handleAddItem()}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  isReordering
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                disabled={isReordering}
              >
                Add Root Item
              </button>
            </div>
          </div>

          <div className='p-6 relative'>
            {allItems.length === 0 ? (
              <div className='text-center py-8 text-gray-500 dark:text-gray-400'>
                No menu items yet. Click "Add Root Item" to get started.
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                {renderMenuTree(allItems)}

                <DragOverlay>
                  {activeId ? (
                    <div className='bg-white dark:bg-gray-700 shadow-lg rounded-lg p-3 opacity-80'>
                      {findMenuItem(allItems, activeId)?.name}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </div>

      {showModal && (
        <MenuItemModal
          item={editingItem}
          onSave={handleSaveItem}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  )
}

export default MenuEditor
